'use client';

import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import { toast } from '@/lib/toast';
import { ApiClientError } from '@/lib/api-client';
import {
  createDevItem,
  getDevItems,
  DevItem,
  DevItemPriority,
  DevItemSeverity,
  DevItemType,
} from '@/services/api/devtracker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { DocumentEditor, type DocumentEditorRef } from '@/features/document-editor';
import type { UserInfo } from '@/components/UserSelector';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { colors, typography } from '@/styles/theme';

const PRIORITY_OPTIONS: DevItemPriority[] = [
  DevItemPriority.P0,
  DevItemPriority.P1,
  DevItemPriority.P2,
  DevItemPriority.P3,
];

const TYPE_OPTIONS: DevItemType[] = [DevItemType.MODULE, DevItemType.FEATURE, DevItemType.BUG];

const SEVERITY_OPTIONS: DevItemSeverity[] = [
  DevItemSeverity.S1,
  DevItemSeverity.S2,
  DevItemSeverity.S3,
  DevItemSeverity.S4,
];

export default function DevItemCreatePage() {
  const { t } = useTranslation();
  const { isReady, user } = useAuthGuard();
  const router = useRouter();
  const editorRef = useRef<DocumentEditorRef>(null);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState('');
  const [owner, setOwner] = useState<UserInfo | null>(null);
  const [modules, setModules] = useState<DevItem[]>([]);
  const [formState, setFormState] = useState({
    title: '',
    itemType: DevItemType.MODULE,
    priority: DevItemPriority.P2,
    severity: DevItemSeverity.S2,
    moduleKey: '',
    parentId: '',
  });

  useEffect(() => {
    if (user && !owner) {
      setOwner({ id: user.id, displayName: user.displayName, email: user.email });
    }
  }, [user, owner]);

  const loadModules = useCallback(async () => {
    try {
      const data = await getDevItems({ page: 1, pageSize: 100, itemType: DevItemType.MODULE });
      setModules(data.items);
    } catch (error) {
      // 忽略模块加载失败，不阻断页面
    }
  }, []);

  useEffect(() => {
    if (!isReady) return;
    loadModules();
  }, [isReady, loadModules]);

  const moduleKeyOptions = useMemo(() => {
    const values = new Set<string>();
    modules.forEach((item) => {
      if (item.moduleKey && item.moduleKey.trim()) {
        values.add(item.moduleKey.trim());
      }
    });
    return Array.from(values);
  }, [modules]);

  const handleCreate = async () => {
    if (!owner) {
      toast.error(t.devtracker?.toast?.requiredFields);
      return;
    }

    if (!formState.title.trim()) {
      toast.error(t.devtracker?.create?.titleRequired);
      return;
    }

    if (formState.itemType !== DevItemType.MODULE && !formState.parentId) {
      toast.error(t.devtracker?.toast?.requiredFields);
      return;
    }

    if (!editorRef.current || editorRef.current.isEmpty()) {
      toast.error(t.devtracker?.toast?.contentRequired);
      return;
    }

    try {
      setSaving(true);
      const created = await createDevItem({
        title: formState.title.trim(),
        itemType: formState.itemType,
        priority: formState.priority,
        severity: formState.itemType === DevItemType.BUG ? formState.severity : undefined,
        moduleKey: formState.moduleKey.trim() || undefined,
        parentId: formState.itemType === DevItemType.MODULE ? undefined : formState.parentId || undefined,
        ownerId: owner.id,
        content: (editorRef.current.getJSON() ?? {}) as Record<string, unknown>,
      });
      toast.success(t.devtracker?.toast?.created);
      router.push(`/devtracker/items/${created.id}`);
    } catch (error) {
      if (error instanceof ApiClientError) {
        toast.error(error.message);
      } else {
        toast.error(t.devtracker?.toast?.createFailed);
      }
    } finally {
      setSaving(false);
    }
  };

  if (!isReady) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-gray-400">
        {t.devtracker?.loading}
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      <div className="bg-white border-b" style={{ borderColor: colors.border }}>
        <div className="px-6 h-12 flex items-center justify-between">
          <h1 className={typography.h4}>{t.devtracker?.create?.centerTitle}</h1>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => router.push('/devtracker/items')}
              className="flex items-center gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              {t.devtracker?.create?.back}
            </Button>
            <Button size="sm" onClick={handleCreate} disabled={saving} className="flex items-center gap-2">
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : null}
              {saving ? t.devtracker?.create?.saving : t.devtracker?.create?.submit}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto w-full px-6">
        <Card>
          <CardContent className="space-y-6">
            <div className="mt-5 rounded-md border border-gray-200 bg-white p-3 space-y-3">
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                <div>
                  <label className="text-sm text-gray-600">{t.devtracker?.fields?.moduleKey}</label>
                  <Input
                    list="devtracker-module-keys"
                    value={formState.moduleKey}
                    onChange={(event) => setFormState((prev) => ({ ...prev, moduleKey: event.target.value }))}
                    placeholder={t.devtracker?.create?.moduleKeyPlaceholder}
                  />
                  <datalist id="devtracker-module-keys">
                    {moduleKeyOptions.map((value) => (
                      <option key={value} value={value} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="text-sm text-gray-600">{t.devtracker?.fields?.itemType}</label>
                  <Select
                    value={formState.itemType}
                    onValueChange={(value) =>
                      setFormState((prev) => ({
                        ...prev,
                        itemType: value as DevItemType,
                        parentId: value === DevItemType.MODULE ? '' : prev.parentId,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TYPE_OPTIONS.map((itemType) => (
                        <SelectItem key={itemType} value={itemType}>
                          {t.devtracker?.itemType?.[itemType]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm text-gray-600">{t.devtracker?.fields?.priority}</label>
                  <Select
                    value={formState.priority}
                    onValueChange={(value) =>
                      setFormState((prev) => ({ ...prev, priority: value as DevItemPriority }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITY_OPTIONS.map((priority) => (
                        <SelectItem key={priority} value={priority}>
                          {t.devtracker?.priority?.[priority]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {formState.itemType !== DevItemType.MODULE && (
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                  {formState.itemType === DevItemType.BUG && (
                    <div>
                      <label className="text-sm text-gray-600">{t.devtracker?.fields?.severity}</label>
                      <Select
                        value={formState.severity}
                        onValueChange={(value) =>
                          setFormState((prev) => ({ ...prev, severity: value as DevItemSeverity }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SEVERITY_OPTIONS.map((severity) => (
                            <SelectItem key={severity} value={severity}>
                              {severity}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <label className="text-sm text-gray-600">{t.devtracker?.fields?.parentId}</label>
                    <Select
                      value={formState.parentId}
                      onValueChange={(value) =>
                        setFormState((prev) => ({ ...prev, parentId: value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t.devtracker?.create?.modulePlaceholder} />
                      </SelectTrigger>
                      <SelectContent>
                        {modules.length === 0 ? (
                          <SelectItem value="__empty" disabled>
                            {t.devtracker?.create?.moduleEmpty}
                          </SelectItem>
                        ) : (
                          modules.map((module) => (
                            <SelectItem key={module.id} value={module.id}>
                              {module.title}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <label className="text-sm text-gray-600">{t.devtracker?.fields?.title}</label>
              <Input
                value={formState.title}
                onChange={(event) => setFormState((prev) => ({ ...prev, title: event.target.value }))}
                placeholder={t.devtracker?.create?.titlePlaceholder}
              />
            </div>
            <DocumentEditor
              ref={editorRef}
              value={content}
              placeholder={t.devtracker?.create?.contentHint}
              onChange={setContent}
              minHeight="420px"
            />
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-400">
                {t.devtracker?.fields?.owner}：{owner?.displayName || '-'}
              </div>
              <Button onClick={handleCreate} disabled={saving}>
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : null}
                {saving ? t.devtracker?.create?.saving : t.devtracker?.create?.submit}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

    </div>
  );
}
